import type { ClientVersion, ErrorCode, ErrorReport } from "@don/wire";

import { parseRoute } from "../routing/route.js";

/**
 * The Console's error reporter (issue 243, PRD "Console reporter").
 *
 * Four triggers, each one closed code: the root error boundary (`ErrorBoundary.tsx`) and an
 * uncaught throw (`window`'s `error` event) are `console_render_crash`, an unhandled promise
 * rejection is `console_unhandled_rejection`, and the backend adapter's 5xx answer or refused
 * fetch is `console_api_5xx` or `console_api_unreachable` (`backend/adapter.ts`).
 *
 * **What is posted is a code and numbers, never what went wrong.** {@link reportOf} builds the
 * body from the code, the build's version and the clock, and nothing a trigger hands in reaches
 * it: the thrown value, its message and stack, the page's URL and route parameters are never
 * read. The server refuses any other key (`wire::ErrorReport`'s `deny_unknown_fields`).
 *
 * **Once per code per page load**, so a render loop or a dead backend is one report rather than
 * a flood; the server's (person, code) hourly throttle is the second bound. **Nothing is posted
 * while {@link ReporterOptions.reportable} says no**, which `main.tsx` sets for the signed-out
 * sign-in page: there is nobody to attribute a report to, and the owner signing in sees it.
 */

/** The four codes the Console sends; the server refuses any other from it. */
export type ConsoleErrorCode = Extract<
  ErrorCode,
  "console_render_crash" | "console_unhandled_rejection" | "console_api_5xx" | "console_api_unreachable"
>;

/** Where the reporter posts: the backend's owner-session route, same origin. */
export const CONSOLE_ERRORS_PATH = "/console-errors";

/**
 * The build's version as the wire takes it: one to four dot-separated integers, or `undefined`
 * for a build with no constant or one that is not that shape, which the server logs `unknown`.
 */
export function appVersionOf(raw: unknown): ClientVersion | undefined {
  if (typeof raw !== "string" || !/^\d{1,9}(\.\d{1,9}){0,3}$/.test(raw)) return undefined;
  return raw.split(".").map(Number);
}

/** One report's body: exactly these keys, `app_version` only when the build has one. */
export function reportOf(code: ConsoleErrorCode, appVersion: ClientVersion | undefined, atMs: number): ErrorReport {
  const report: ErrorReport = {
    code,
    source: "console",
    first_at_ms: atMs,
    last_at_ms: atMs,
    count: 1,
  };
  if (appVersion !== undefined) report.app_version = [...appVersion];
  return report;
}

/**
 * Whether a report may be posted from `pathname`: anywhere but the signed-out sign-in page
 * (PRD "Console reporter"; accepted, the owner signing in sees an error there) and the
 * employee's Connections page, whose reader holds no owner session for the report's
 * owner-authenticated sink (issue 392, design-spec §5.5 D12), nor the IT admin on the admin
 * callback page (issue 466, Outlook design-spec W8).
 */
export function reportableAt(pathname: string): boolean {
  const kind = parseRoute(pathname).kind;
  return kind !== "sign-in" && kind !== "connect" && kind !== "admin-callback";
}

export interface ReporterOptions {
  /** Send one report. `main.tsx` passes a `keepalive` `fetch`; a failed post is dropped. */
  post(report: ErrorReport): void;
  /** The build's version (`appVersionOf(__CONSOLE_VERSION__)`), or `undefined`. */
  readonly appVersion: ClientVersion | undefined;
  /** Wall-clock milliseconds. */
  now(): number;
  /** Whether a report may be posted now: `main.tsx` passes {@link reportableAt}. */
  reportable(): boolean;
}

/** The two global events the reporter listens to. A `Window` is one. */
export interface ErrorEvents {
  addEventListener(type: "error" | "unhandledrejection", listener: () => void): void;
  removeEventListener(type: "error" | "unhandledrejection", listener: () => void): void;
}

export interface Reporter {
  /** Report `code`, unless it was already reported this load or the page is not reportable. */
  report(code: ConsoleErrorCode): void;
  /** Listen for uncaught throws and unhandled rejections on `target`. Returns the removal. */
  install(target: ErrorEvents): () => void;
}

export function createReporter(options: ReporterOptions): Reporter {
  const sent = new Set<ConsoleErrorCode>();
  function report(code: ConsoleErrorCode): void {
    // A code skipped on the sign-in page is not marked sent: the same failure after sign-in
    // is still reported once.
    if (sent.has(code) || !options.reportable()) return;
    sent.add(code);
    try {
      options.post(reportOf(code, options.appVersion, options.now()));
    } catch {
      // Reporting never becomes a second failure on the page.
    }
  }
  return {
    report,
    install(target) {
      // The listeners take no argument on purpose: the event carries the message, the stack
      // and the source URL, and none of it is read.
      const crashed = () => report("console_render_crash");
      const rejected = () => report("console_unhandled_rejection");
      target.addEventListener("error", crashed);
      target.addEventListener("unhandledrejection", rejected);
      return () => {
        target.removeEventListener("error", crashed);
        target.removeEventListener("unhandledrejection", rejected);
      };
    },
  };
}

/**
 * A `post` for {@link ReporterOptions}: `POST {base}/console-errors` with `keepalive`, so a
 * report sent as the page unloads still leaves. Same origin, so the owner-session cookie and
 * `Origin` go with it. Not through the backend adapter: a failed report must not be reported.
 */
export function postWith(fetchImpl: typeof fetch, base: string): (report: ErrorReport) => void {
  return (report) => {
    void fetchImpl(`${base}${CONSOLE_ERRORS_PATH}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(report),
      keepalive: true,
    }).catch(() => undefined);
  };
}
